import React, { useState, useEffect } from 'react';
import { Modal } from '@/components';
import {
  BoxV2 as Box,
  Button,
  Flex,
  MultiSelector,
  TextInput,
} from 'portal-commons';
import { campaignController, RejectCampaignParams } from '@/apis/core';
import { toastFlashMessage } from '@/utils';
import { SelectItem } from '@/utils/types';
import { useController, useForm } from 'react-hook-form';
import {
  createMaxLengthValidator,
  createRequiredValidator,
  isValidInput,
} from '../../validators';

interface Props {
  open: boolean;
  campaign: {
    uid: string;
    brandUid?: string;
    downstreamCnpName?: string;
  };
  categories: SelectItem[];
  onClose: (reloadFlag?: boolean) => void;
}

interface FormValues {
  explanation: string;
  rejectionCategory: string[];
}

const DeclineCnpRcsModal: React.FC<Props> = ({
  open,
  campaign,
  categories = [],
  onClose,
}) => {
  const [disabled, setDisabled] = useState(false);

  const { control, reset, handleSubmit } = useForm<FormValues>({
    mode: 'onTouched',
    defaultValues: {
      explanation: '',
      rejectionCategory: [],
    },
  });

  const {
    field: { value: category, onChange: setCategory },
    fieldState: { error: categoryError },
  } = useController({
    control,
    name: 'rejectionCategory',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please select a category'),
      },
    },
  });

  const {
    field: {
      value: explanation,
      onChange: setExplanation,
      onBlur: validateExplanation,
    },
    fieldState: { error: explanationError },
  } = useController({
    control,
    name: 'explanation',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please enter Explanation'),
        isValidMaxLength: createMaxLengthValidator(2048),
        isValidInput,
      },
    },
  });

  const onValidHandler = async (data: FormValues) => {
    setDisabled(true);

    const response = await campaignController.rejectSharedCampaign(
      campaign.uid,
      data as RejectCampaignParams
    );
    if (response) {
      toastFlashMessage(`Campaign rejected successfully`, 'success');
      onClose(true);
    }
    setDisabled(false);
  };

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  return (
    <Modal open={open} testId="declineCnpRcsModal">
      <Box
        as="h2"
        sx={{
          my: 12,
          fontSize: 'H600',
          fontWeight: 600,
          lineHeight: '28px',
          color: 't.black80',
        }}
      >
        Decline Campaign
      </Box>
      <form
        noValidate
        onSubmit={handleSubmit(onValidHandler)}
        style={{ width: '100%' }}
      >
        <Flex sx={{ flexDirection: 'column', gap: 'l' }}>
          <TextInput readOnly value={campaign.uid} label="Campaign ID" />
          <TextInput readOnly value={campaign.brandUid} label="Brand ID" />
          <TextInput
            readOnly
            value={campaign?.downstreamCnpName || ''}
            label="Downstream CNP"
          />
          <Box sx={{ width: '100%', mt: '-4px' }}>
            <MultiSelector
              disabled={disabled}
              options={categories}
              data-testid="rejectCategory"
              label="Category *"
              placeholder="Category"
              value={category[0]}
              error={!!categoryError?.message}
              helperText={categoryError?.message}
              onChange={(value: string) => {
                if (value.length) {
                  const categoryArray = value.split(',');
                  setCategory(categoryArray);
                } else {
                  setCategory([]);
                }
              }}
            />
          </Box>
          <TextInput
            required
            data-testid="rejectExplanation"
            label="Explanation"
            placeholder="Please enter Explanation"
            disabled={disabled}
            readOnly={disabled}
            value={explanation}
            maxLength={2048}
            error={!!explanationError?.message}
            note={explanationError?.message}
            onChange={setExplanation}
            onBlur={validateExplanation}
          />
        </Flex>
        <Flex sx={{ py: 30, gap: 18, justifyContent: 'center' }}>
          <Button
            variant="outline"
            data-testid="electCnpEbmModalCancelButton"
            disabled={disabled}
            onClick={() => onClose(false)}
          >
            Cancel
          </Button>
          <Button
            disabled={disabled}
            data-testid="electCnpEbmModalSubmitButton"
            type="submit"
          >
            Decline
          </Button>
        </Flex>
      </form>
    </Modal>
  );
};

export default DeclineCnpRcsModal;
